import { Suspense } from 'react';
import { Outlet, useMatches } from 'react-router-dom';
import { useNamespaces } from '@parcel2go/npm-p2g-app';
import { ConcernsContextProvider } from '~/apps/smartsend/lib/context/ConcernsContext';
import { DownloadContextProvider } from '~/apps/smartsend/lib/context/DownloadContext';
import { SmartSendContextProvider } from '~/apps/smartsend/lib/context/SmartSendContext';
import EventEmitterRC from '~/apps/smartsend/lib/em/EventEmitterRC';
import { useAuth } from 'react-oidc-context';
import { P2GImageMessage } from '~/components/imagemessage';
import { NavigationContextProvider } from '~/components/nav/nav-context';
import { ProfileContextProvider } from '~/apps/smartsend/lib/context/ProfileContext';

// Provider/auth shell shared by both navigation experiences. The per-experience
// chrome (sidebar, banners, tasks) lives in the layout rendered into <Outlet/>:
// SmartSendLayout for the business experience, ConsumerLayout for the consumer view.
type RouteHandle = { namespaces?: string[] };

export default function Component() {
  const { isAuthenticated } = useAuth();

  // Only the navigation chrome's namespace is loaded up front (CORE_TRANSLATION_NAMESPACES);
  // everything else is declared per route as `handle.namespaces` in main.tsx. Prefetching here,
  // above this component's <Suspense>, starts a section's namespaces together rather than letting
  // each descendant suspend and fetch its own in turn.
  const matches = useMatches();
  useNamespaces(matches.flatMap((match) => (match.handle as RouteHandle | undefined)?.namespaces ?? []));

  return (
    <Suspense fallback={<></>}>
      {isAuthenticated ? (
        <NavigationContextProvider>
          <EventEmitterRC>
            <DownloadContextProvider>
              <SmartSendContextProvider>
                <ProfileContextProvider>
                  <ConcernsContextProvider>
                    <Outlet />
                  </ConcernsContextProvider>
                </ProfileContextProvider>
              </SmartSendContextProvider>
            </DownloadContextProvider>
          </EventEmitterRC>
        </NavigationContextProvider>
      ) : (
        <P2GImageMessage
          image="/theme/smartsend/notifications/signing-in.png"
          contentKey="image-myaccount-signing-in"
          message="Signing you in"
          showLoader
        />
      )}
    </Suspense>
  );
}

Component.displayName = 'My Account';
